import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Building2, Lock, CheckCircle2, Loader2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Missing reset token in URL");
      return;
    }
    if (pw !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (pw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated — sign in with your new password.");
      setTimeout(() => navigate("/login/admin"), 1500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center" data-testid="reset-missing-token">
          <div className="h-11 w-11 rounded-md bg-red-600 text-white flex items-center justify-center mx-auto mb-6">
            <Lock className="h-5 w-5" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-950">Invalid reset link</h2>
          <p className="mt-2 text-slate-600 text-sm">
            This link is missing a reset token. Please request a new password reset.
          </p>
          <Link
            to="/forgot-password"
            className="mt-6 inline-block text-blue-600 hover:underline font-medium"
            data-testid="reset-request-new"
          >
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <Link
          to="/login/admin"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8"
          data-testid="reset-back-to-login"
        >
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>

        <div className="h-11 w-11 rounded-md bg-slate-950 text-white flex items-center justify-center mb-6">
          <Building2 className="h-5 w-5" />
        </div>

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Rana Associates</p>
        <h2 className="mt-2 font-heading text-3xl font-bold text-slate-950">Choose a new password</h2>
        <p className="mt-2 text-slate-600 text-sm">
          Use 8+ characters with at least one letter and one digit.
        </p>

        {done ? (
          <div
            className="mt-8 rounded-md border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
            data-testid="reset-success"
          >
            <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-900">
              Password updated successfully. Redirecting you to sign in…
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
            <div>
              <Label htmlFor="rp-new">New password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="rp-new"
                  type="password"
                  required
                  minLength={8}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="At least 8 characters"
                  className="pl-9"
                  data-testid="reset-new-password-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="rp-confirm">Confirm new password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="rp-confirm"
                  type="password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Retype the password"
                  className="pl-9"
                  data-testid="reset-confirm-password-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-slate-950 hover:bg-slate-800 text-white h-11"
              data-testid="reset-submit-button"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
